import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, completeMfa, completeNewPassword, getToken } from '../auth';

export default function Login({ setToken }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState('login'); // login | mfa | newPassword
  const [cognitoUser, setCognitoUser] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn(email, password);
      if (result.type === 'success') {
        const token = await getToken();
        setToken(token);
        navigate('/admin');
      } else if (result.type === 'mfa') {
        setCognitoUser(result.user);
        setStep('mfa');
      } else if (result.type === 'newPassword') {
        setCognitoUser(result.user);
        setStep('newPassword');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    }
    setLoading(false);
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeMfa(cognitoUser, mfaCode);
      const token = await getToken();
      setToken(token);
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Invalid MFA code');
    }
    setLoading(false);
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await completeNewPassword(cognitoUser, newPassword);
      if (result?.type === 'mfaSetup') {
        setError('Please set up MFA in your authenticator app, then try logging in again.');
        setStep('login');
      } else {
        const token = await getToken();
        setToken(token);
        navigate('/admin');
      }
    } catch (err) {
      setError(err.message || 'Failed to set new password');
    }
    setLoading(false);
  };

  const headings = {
    login: 'Admin sign in',
    mfa: 'Two-factor code',
    newPassword: 'Set a new password',
  };

  return (
    <div className="auth-card">
      <div className="auth-card__head">
        <p className="eyebrow">Usman&rsquo;s Garden</p>
        <h1>{headings[step]}</h1>
      </div>

      {error && <p className="form-error form-error--banner" role="alert">{error}</p>}

      {step === 'login' && (
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      )}

      {step === 'mfa' && (
        <form onSubmit={handleMfa}>
          <p className="auth-card__hint">Enter the code from your authenticator app.</p>
          <div className="form-group">
            <label htmlFor="mfaCode">Authentication code</label>
            <input id="mfaCode" type="text" value={mfaCode} onChange={e => setMfaCode(e.target.value)} required autoComplete="one-time-code" inputMode="numeric" />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={handleNewPassword}>
          <p className="auth-card__hint">Choose a new password of at least 12 characters.</p>
          <div className="form-group">
            <label htmlFor="newPassword">New password</label>
            <input id="newPassword" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required autoComplete="new-password" minLength={12} />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Setting password...' : 'Set password'}
          </button>
        </form>
      )}
    </div>
  );
}
